import axios from 'axios';
import { Client, Protocol } from '@tencent/mega-ipc-renderer';
import { MegaMediaServiceProtocol, IImageResult } from '@tencent/mega-sdk-protocols';
import i18n from '@i18next/i18n';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import { getQuerySearchParams } from '@src/slide/infrastructure/module/independent/url/param-operation';
import { getCacConfigByKey } from '@src/slide/infrastructure/module/dependent/config/tob/app-config';

const baseURL = getCacConfigByKey('custom.image.uploadBaseURL', '/api/docsdata/image/upload');

const cgi = axios.create({
    baseURL,
});

// 移动端本地编辑上传图片需带图片信息
interface IFileInfo {
    name: string;
    lastModified: number;
}

/**
 * 上传文件参数
 */
interface IUploadImageBlob {
    blob: Blob;
    fileInfo?: IFileInfo;
    onProgress?: (percent: number) => void;
    extraParams?: {
        base64: boolean;
        usage?: ImageUsage;
    };
}

export interface CDNImage {
    url: string;
    type: string;
    size: number;
    width: number;
    height: number;
}

class ErrorTips {
    public static tips = {
        default: i18n.t('上传图片失败，请稍后再试。'),
        svg: i18n.t('暂不支持上传SVG格式图片。'),
        permission: i18n.t('暂无权限上传图片，请重新登录后再试。'),
        broken: i18n.t('上传图片失败，图片损坏或不支持该格式。'),
        size: i18n.t('图片体积太大，请压缩后再试。'),
        network: i18n.t('网络异常，不支持插入图片，请在网络正常后再试。'),
        outOfSpace: i18n.t('当前空间已满，请升级个人存储空间。'),
    };

    private static map = new Map<number, string>([
        [90003, ErrorTips.tips.default],
        [90011, ErrorTips.tips.default],
        [90012, ErrorTips.tips.svg],
        [90013, ErrorTips.tips.svg],
        [90001, ErrorTips.tips.permission],
        [10003, ErrorTips.tips.permission],
        [90008, ErrorTips.tips.broken],
        [90010, ErrorTips.tips.size],
        [20010, ErrorTips.tips.outOfSpace],
        [20013, ErrorTips.tips.outOfSpace],
    ]);

    public static get(errorCode: number) {
        return this.map.get(errorCode) ?? ErrorTips.tips.default;
    }
}

/**
 * 安全内审：《正文内普通图片分享、以及文档导出的安全问题》
 * @see https://tapd.woa.com/TIM_web/prong/stories/view/1010143861878511893)
 *
 * 图片上传需要制定用途来区分不同的策略，
 * 例如：上传到正文的图片，会接入腾讯云的防盗链方案。
 */
export enum ImageUsage {
    Unspecified = 0, // 未指定
    DocumentBody = 1, // 用于文档正文
    DocumentSnapshot = 2, // 用于文档截图
    VideoSnapshot = 3, // 用于视频截图
    FillinForm = 4, // 用于填写收集表
}

/**
 * 没事儿别直接 import 这个方法，正规点儿就走 image-edit-manager。
 * 漏产品需求或者出Bug，后果自负。
 */
export async function uploadImageBlob(params: IUploadImageBlob): Promise<CDNImage> {
    const {
        blob,
        fileInfo = { name: '', lastModified: 0 },
        extraParams = { base64: false, usage: ImageUsage.DocumentBody },
        onProgress,
    } = params;
    if (ENV.IS_MOBILE_LOCAL_EDIT) {
        const cdnImage = await MobileLocalEditUpload.uploadImageBlob({ blob, fileInfo });
        return cdnImage as CDNImage;
    }
    const globalPadId = window?.clientVars?.globalPadId;
    const form = new FormData();
    form.append('file', blob);

    if (extraParams.base64) {
        form.append('isBase64', '1');
    }

    const { data } = await cgi
        .post('', form, {
            params: {
                globalPadId,
                usage: extraParams.usage,
                path: (blob as any)?.path ?? '',
            },
            onUploadProgress(progress) {
                progress.progress && onProgress?.(progress.progress);
            },
        })
        .catch((reason) => {
            const error = new Error(ErrorTips.tips.network);
            Object.assign(error, { reason, isNetworkError: true });
            throw error;
        });

    const { retcode, size, url, width, height } = data;
    if (url && retcode === 0) {
        return {
            size,
            type: blob.type,
            url,
            width,
            height,
        };
    }
    const error = new Error(ErrorTips.get(retcode));
    Object.assign(error, { retcode, skipRetry: retcode === 20013 || retcode === 20010 });
    throw error;
}

// 基础设施层不允许跨模块引用，移动端本地编辑上传图片逻辑单独实现
class MobileLocalEditUpload {
    public static async uploadImageBlob(params: IUploadImageBlob) {
        const { blob, fileInfo = { name: '', lastModified: 0 } } = params;
        const client = this.getClient();
        const form = new FormData();
        form.append('file', blob);
        const { code, data, message }: IImageResult = await client.media.uploadImage({
            data: form,
            params: {
                ...fileInfo,
                globalPadId: window?.clientVars?.globalPadId,
                localFilePath: getQuerySearchParams('localFilePath'),
            },
        });
        if (code === 0) {
            return {
                url: data?.url,
                type: blob.type,
            };
        }
        return Promise.reject({
            code,
            message,
        });
    }

    private static getClient() {
        return Client.create(
            {
                media: Protocol<MegaMediaServiceProtocol>(),
            },
            ENV.IS_MOBILE_LOCAL_EDIT,
        );
    }
}
